// MCP piekļuve AI aģentiem: OAuth pieslēgšana (ChatGPT u.c.) ar pieslēgto lietotņu sarakstu un atvienošanu (ADR 0014);
// personīgo žetonu izveide (redzams vienreiz), saraksts un atsaukšana.

import { useEffect, useState } from 'react';
import { fmtStamp } from '@balansa/shared';
import { api } from '../lib/api';
import { useApp } from '../store';
import { Modal } from './Modal';

interface GrantInfo {
  id: string;
  clientName: string;
  host: string;
  kind: 'dcr' | 'cimd';
  createdAt: number;
  lastUsedAt: number | null;
}

interface TokenInfo {
  id: string;
  name: string;
  prefix: string;
  createdAt: number;
  lastUsedAt: number | null;
}

function CopyField({ label, value, testId }: { label: string; value: string; testId?: string }) {
  const [done, setDone] = useState(false);
  return (
    <div className="field">
      <span className="lbl-t">{label}</span>
      <div className="mcp-copy">
        <input className="inp mono" readOnly value={value} onFocus={(e) => e.currentTarget.select()} data-testid={testId} aria-label={label} />
        <button
          type="button"
          className="btn sm"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setDone(true);
              setTimeout(() => setDone(false), 1500);
            } catch {
              /* pārlūks neļauj — lietotājs var iezīmēt un kopēt pats */
            }
          }}
        >
          {done ? 'Nokopēts ✓' : 'Kopēt'}
        </button>
      </div>
    </div>
  );
}

export function McpDialog() {
  const A = useApp.getState;
  const close = () => A().closeDialog();
  const [tokens, setTokens] = useState<TokenInfo[] | null>(null);
  const [grants, setGrants] = useState<GrantInfo[] | null>(null);
  const [name, setName] = useState('Claude');
  const [created, setCreated] = useState<{ name: string; token: string } | null>(null);
  const [err, setErr] = useState('');
  const url = `${location.origin}/mcp`;

  const load = async () => {
    try {
      const [t, g] = await Promise.all([api.get<{ tokens: TokenInfo[] }>('/mcp-tokens'), api.get<{ grants: GrantInfo[] }>('/oauth-grants')]);
      setTokens(t.tokens);
      setGrants(g.grants);
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const config = created
    ? JSON.stringify({ mcpServers: { 'balansa-aplis': { type: 'http', url, headers: { Authorization: `Bearer ${created.token}` } } } }, null, 2)
    : '';

  return (
    <Modal onClose={close} className="wide" label="MCP piekļuve" testId="mcp-dialog">
      <div className="dlg mcp">
        <h3>MCP piekļuve AI aģentiem</h3>
        <p className="muted" style={{ margin: 0 }}>
          Ar MCP savienojumu AI aģents (piem. ChatGPT vai Claude) var strādāt ar tavu apli tāpat kā tu: <b>veidot un pielāgot aktivitāšu kārtis</b>, mainīt
          jomas, saites un enerģijas modeli, izspēlēt kārtis, veidot pārskatus. Aģents saņem arī instrukciju, kā šis produkts darbojas.
        </p>

        <div>
          <div className="cd-sec">Servera adrese</div>
          <code className="mcp-url" data-testid="mcp-url">{url}</code>
          <p className="cd-hint">Transports: Streamable HTTP. Autentifikācija: OAuth (ieteicams) vai personīgais žetons.</p>
        </div>

        <div className="mcp-oauth" data-testid="mcp-oauth">
          <div className="cd-sec">ChatGPT un citi aģenti ar OAuth — bez žetona</div>
          <ol className="mcp-steps">
            <li>
              ChatGPT: <b>Plugins → + → Add custom MCP server</b>; Claude: <b>Settings → Connectors → Add custom connector</b>.
            </li>
            <li>
              Servera adrese: <code>{url}</code>, autentifikācija: <b>OAuth</b> (klienta ID un noslēpums nav jānorāda).
            </li>
            <li>Atvērsies Balansa apļa lapa — pārbaudi lietotni un adresi (piem. chatgpt.com) un spied <b>Atļaut</b>.</li>
          </ol>
          <div className="cd-sec">Pieslēgtās lietotnes</div>
          {grants === null ? (
            <p className="muted">Ielādē…</p>
          ) : !grants.length ? (
            <p className="muted" data-testid="oauth-empty">
              Vēl nav pieslēgta neviena lietotne.
            </p>
          ) : (
            <ul className="mcp-list" data-testid="oauth-list">
              {grants.map((g) => (
                <li key={g.id}>
                  <div>
                    <b>{g.clientName}</b> <code>{g.host}</code>
                    <small>
                      pieslēgta {fmtStamp(g.createdAt)} · {g.lastUsedAt ? 'lietota ' + fmtStamp(g.lastUsedAt) : 'vēl nav lietota'}
                    </small>
                  </div>
                  <button
                    type="button"
                    className="btn sm ghost danger"
                    onClick={async () => {
                      const ok = await A().ask({
                        title: `Atvienot “${g.clientName}”?`,
                        message: 'Lietotne vairs nevarēs piekļūt tavam aplim. Lai to lietotu atkal, pieslēdz to no jauna.',
                        ok: 'Atvienot',
                        danger: true
                      });
                      if (!ok) return;
                      await api.del(`/oauth-grants/${g.id}`);
                      await load();
                    }}
                  >
                    Atvienot
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="cd-sec">Personīgais žetons (OpenAI API, Codex, Claude Desktop u.c.)</div>

        {created ? (
          <div className="mcp-new" data-testid="mcp-new-token">
            <p className="imp-warn">Žetons “{created.name}” ir redzams tikai tagad — nokopē to. Tas dod pilnu piekļuvi tavam aplim, glabā to kā paroli.</p>
            <CopyField label="Žetons" value={created.token} testId="mcp-token-value" />
            <CopyField label="Savienojuma URL ar žetonu (Claude pielāgotais savienotājs u.c.)" value={`${url}?token=${created.token}`} />
            <div className="field">
              <span className="lbl-t">Klientiem, kas atbalsta galvenes (ieteicams — žetons nenonāk URL žurnālos)</span>
              <pre className="mcp-pre">{config}</pre>
            </div>
            <div className="dlg-row">
              <button type="button" className="btn sm" onClick={() => setCreated(null)}>
                Gatavs
              </button>
            </div>
          </div>
        ) : (
          <form
            className="mcp-create"
            onSubmit={async (e) => {
              e.preventDefault();
              setErr('');
              try {
                const r = await api.post<TokenInfo & { token: string }>('/mcp-tokens', { name: name.trim() || 'AI aģents' });
                setCreated({ name: r.name, token: r.token });
                await load();
              } catch (e2) {
                setErr((e2 as Error).message);
              }
            }}
          >
            <div className="field">
              <label htmlFor="mcp-name">Jauna žetona nosaukums</label>
              <input id="mcp-name" className="inp" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} placeholder="piem. Claude Desktop" />
            </div>
            <button type="submit" className="btn sm primary" data-testid="mcp-create">
              Izveidot žetonu
            </button>
          </form>
        )}

        {err ? <p className="bad">{err}</p> : null}

        <p className="cd-hint" style={{ margin: 0 }}>
          Žetons: <code>Authorization: Bearer &lt;žetons&gt;</code> vai <code>?token=&lt;žetons&gt;</code>. OpenAI Responses API — lauks{' '}
          <code>authorization</code> rīkā <code>{'{ type: "mcp" }'}</code>.
        </p>

        <div>
          <div className="cd-sec">Aktīvie žetoni</div>
          {tokens === null ? (
            <p className="muted">Ielādē…</p>
          ) : !tokens.length ? (
            <p className="muted" data-testid="mcp-empty">
              Vēl nav neviena žetona.
            </p>
          ) : (
            <ul className="mcp-list" data-testid="mcp-list">
              {tokens.map((t) => (
                <li key={t.id}>
                  <div>
                    <b>{t.name}</b> <code>{t.prefix}…</code>
                    <small>
                      izveidots {fmtStamp(t.createdAt)} · {t.lastUsedAt ? 'lietots ' + fmtStamp(t.lastUsedAt) : 'vēl nav lietots'}
                    </small>
                  </div>
                  <button
                    type="button"
                    className="btn sm ghost danger"
                    onClick={async () => {
                      const ok = await A().ask({
                        title: `Atsaukt žetonu “${t.name}”?`,
                        message: 'Aģenti, kas to izmanto, vairs nevarēs piekļūt tavam aplim.',
                        ok: 'Atsaukt',
                        danger: true
                      });
                      if (!ok) return;
                      await api.del(`/mcp-tokens/${t.id}`);
                      await load();
                    }}
                  >
                    Atsaukt
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="dlg-actions">
          <span className="sp" />
          <button type="button" className="btn sm" onClick={close}>
            Aizvērt
          </button>
        </div>
      </div>
    </Modal>
  );
}
